import { useCallback, useState } from 'react';
import { usePathname } from 'next/navigation';
import { sendGTMEvent } from '@next/third-parties/google';
import useCopyToClipBoard from '@/hooks/useCopyToClipboard';
import { API_URL } from '../../constants';
import { Vendor } from '@/lib/types/interfaces/Vendor';

interface ContactsState {
  isPhoneNumber: boolean;
  isCopyUrl: boolean;
  isShowTooltip: boolean;
  isShowEmail: boolean;
}

interface Props {
  service: Vendor
}

const useCopyContacts = ({ service }: Props) => {
  const pathname = usePathname();
  const { copy } = useCopyToClipBoard();
  const [contacts, setContacts] = useState<ContactsState>({
    isPhoneNumber: false,
    isCopyUrl: false,
    isShowTooltip: false,
    isShowEmail: false,
  });

  const handleCopyUrl = useCallback(async () => {
    await copy(`${API_URL}${pathname}`);
    setContacts(prevState => ({ ...prevState, isCopyUrl: true }));
    sendGTMEvent({
      url: pathname,
      event: 'click_on_service_provider_copy_url',
      service_provider_name: service.business_name,
    });
    setTimeout(() => {
      setContacts(prevState => ({ ...prevState, isCopyUrl: false }));
    }, 500);
  }, [copy, pathname]);

  const handleCopyNumber = useCallback(async (number: string) => {
    await copy(number);
    setContacts(prevState => ({ ...prevState, isShowTooltip: true }));
    sendGTMEvent({
      url: pathname,
      event: 'click_on_service_provider_phone_number',
      service_provider_name: service.business_name,
    });

    setTimeout(() => {
      setContacts(prevState => ({ ...prevState, isShowTooltip: false }));
    }, 500);
  }, [copy]);

  const handlePhoneNumber = () => {
    setContacts(prevState => ({ ...prevState, isPhoneNumber: true }));
    sendGTMEvent({
      url: pathname,
      event: 'click_on_service_provider_view_phone_number',
      service_provider_name: service.business_name,
    });
  };

  const handleEmail = () => {
    setContacts(prevState => ({ ...prevState, isShowEmail: true }));
    sendGTMEvent({
      url: pathname,
      event: 'click_on_service_provider_view_email',
      service_provider_name: service.business_name,
    });
  };

  return {
    contacts,
    setContacts,
    handleEmail,
    handleCopyUrl,
    handleCopyNumber,
    handlePhoneNumber,
  };
};
export default useCopyContacts;
